import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface ChildLink {
  id: string;
  parentId: string;
  childId: string;
  relationship: string;
  status: string;
  requestedAt: string;
  approvedAt?: string;
  parent?: { id: string; firstName: string; lastName: string; email: string };
  child?: { id: string; firstName: string; lastName: string; email: string; profileType: string };
}

const ParentDashboard: React.FC = () => {
  const { user, actingAs, switchToChild, switchBackToParent } = useAuth();
  const [children, setChildren] = useState<ChildLink[]>([]);
  const [pendingFromParents, setPendingFromParents] = useState<ChildLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [childEmail, setChildEmail] = useState('');
  const [relationship, setRelationship] = useState('parent');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, parentsRes] = await Promise.all([
        api.get('/parent-child/my-children'),
        api.get('/parent-child/my-parents'),
      ]);
      setChildren(childrenRes.data.links || []);
      setPendingFromParents(
        (parentsRes.data.links || []).filter((l: ChildLink) => l.status === 'pending')
      );
    } catch (err) {
      console.error('Error fetching parent data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/parent-child/link-request', {
        childEmail,
        relationship,
      });
      setChildEmail('');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send link request');
    }
  };

  const handleApprove = async (linkId: string) => {
    try {
      await api.patch(`/parent-child/${linkId}/approve`);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleReject = async (linkId: string) => {
    try {
      await api.patch(`/parent-child/${linkId}/reject`);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject');
    }
  };

  const handleUnlink = async (linkId: string) => {
    if (!window.confirm('Remove this link?')) return;
    try {
      await api.delete(`/parent-child/${linkId}`);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unlink');
    }
  };

  const handleActAs = async (childId: string) => {
    try {
      await switchToChild(childId);
    } catch (err: any) {
      alert('Failed to switch to child account');
    }
  };

  if (loading) return <LoadingSpinner />;

  const approvedChildren = children.filter((c) => c.status === 'approved');
  const pendingChildren = children.filter((c) => c.status === 'pending');

  return (
    <div className="container">
      <h1>Parent Dashboard</h1>

      {actingAs && (
        <div
          style={{
            background: '#E3F2FD',
            padding: '15px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            You are currently acting as <strong>{actingAs.firstName} {actingAs.lastName}</strong>
          </span>
          <button className="btn btn-secondary" onClick={switchBackToParent}>
            Switch Back
          </button>
        </div>
      )}

      {/* Pending requests from parents (if user is a child) */}
      {pendingFromParents.length > 0 && (
        <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid #FF9800' }}>
          <h2>Pending Parent Link Requests</h2>
          {pendingFromParents.map((link) => (
            <div
              key={link.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid #E0E0E0',
              }}
            >
              <div>
                <strong>{link.parent?.firstName} {link.parent?.lastName}</strong> ({link.parent?.email})
                <span style={{ marginLeft: '10px', color: '#757575' }}>wants to link as {link.relationship}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-success btn-sm" onClick={() => handleApprove(link.id)}>
                  Approve
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleReject(link.id)}>
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link child form */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2>Link a Child Account</h2>
        <form onSubmit={handleLinkRequest} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Child's Email</label>
            <input
              type="email"
              value={childEmail}
              onChange={(e) => setChildEmail(e.target.value)}
              placeholder="Enter child's email address"
              required
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Relationship</label>
            <select value={relationship} onChange={(e) => setRelationship(e.target.value)}>
              <option value="parent">Parent</option>
              <option value="guardian">Guardian</option>
              <option value="other">Other</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary" style={{ marginBottom: '1px' }}>
            Send Link Request
          </button>
        </form>
      </div>

      {/* Pending child links */}
      {pendingChildren.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Pending Requests</h2>
          {pendingChildren.map((link) => (
            <div
              key={link.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid #E0E0E0',
              }}
            >
              <div>
                <strong>{link.child?.firstName} {link.child?.lastName}</strong> ({link.child?.email})
                <span className="badge badge-warning" style={{ marginLeft: '10px' }}>Pending</span>
              </div>
              <button className="btn btn-danger btn-sm" onClick={() => handleUnlink(link.id)}>
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Approved children */}
      <div className="card">
        <h2>Linked Children ({approvedChildren.length})</h2>
        {approvedChildren.length === 0 ? (
          <p style={{ color: '#757575' }}>No linked children yet. Send a link request above.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
            {approvedChildren.map((link) => (
              <div
                key={link.id}
                className="card"
                style={{ padding: '15px', margin: 0 }}
              >
                <h3 style={{ marginBottom: '5px' }}>
                  {link.child?.firstName} {link.child?.lastName}
                </h3>
                <p style={{ color: '#757575', marginBottom: '10px' }}>
                  {link.child?.email} &bull; {link.child?.profileType} &bull; {link.relationship}
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleActAs(link.childId)}>
                    Act As
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleUnlink(link.id)}>
                    Unlink
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
